import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  OrganizationTypeConflictListDto,
  ResolveOrganizationTypeConflictRequest,
  ResolveOrganizationTypeConflictResponse,
  DismissOrganizationTypeConflictRequest,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiResponse } from './apiFetch';
import { financeQueries, queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { CLIENT_CONFLICT_MARKER } from '../../lib/clientConflict';
import { writeContactVersion } from './useFinanceContacts';

// Issue #350 — the treasurer-facing data path for the inline contact-page fix-it alert that
// surfaces a contact-merge OrganizationType conflict (#348). Because BOTH candidate values were
// recorded, resolving is a single tap: "It's {kept}" / "It's {discarded}" / "Dismiss". State is
// audit-derived and shared with the filing preflight, so resolving/dismissing clears both.
//
// Backend: GET the list, POST /{contactId}/resolve (200 with the contact's fresh token; 400 bad choice, 409 stale, 404 none open),
// POST /{contactId}/dismiss (204). Multi-tenancy: the shared `authenticatedFetch` interceptor adds
// the X-Subsidiary + X-Campaign-Id headers; every key is cid()-scoped.

/**
 * Every open contact-merge OrganizationType conflict in the campaign. `staleTime: 30_000` mirrors
 * the duplicate-flag hook; a resolve / dismiss invalidates it explicitly.
 */
export function useOrganizationTypeConflicts() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.organizationTypeConflicts(),
    queryFn: () =>
      apiJson<OrganizationTypeConflictListDto>('/api/finance/organization-type-conflicts', {
        error: 'Failed to fetch organization-type conflicts',
      }),
    staleTime: 30_000,
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The invalidation shared by resolve + dismiss. Both close a conflict, which changes the
// conflict list (the alert + the contacts-list count), the affected contact's detail + the
// contacts list, and any dashboard count. financeQueries already matches all those
// finance-scoped surfaces, so a curated
// organizationTypeConflicts()/contacts.all call alongside it would be a provable no-op.
function invalidateResolved(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries(financeQueries);
}

/**
 * POST /{contactId}/resolve — "It's {type}". Sets the survivor's OrganizationType to the chosen
 * candidate and records the resolution, returning the contact's fresh token. A stale token 409s.
 */
export function useResolveOrganizationTypeConflict() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contactId,
      ...body
    }: { contactId: string } & ResolveOrganizationTypeConflictRequest) => {
      const response = await apiResponse(
        `/api/finance/organization-type-conflicts/${contactId}/resolve`,
        { method: 'POST', json: body },
      );
      if (response.status === 409) throw new Error(CLIENT_CONFLICT_MARKER);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to resolve organization-type conflict');
      }
      return (await response.json()) as ResolveOrganizationTypeConflictResponse;
    },
    onSuccess: ({ concurrencyVersion }, { contactId }) => {
      writeContactVersion(queryClient, contactId, concurrencyVersion);
      invalidateResolved(queryClient);
    },
  });
}

/**
 * POST /{contactId}/dismiss — records the dismissal without changing the type (204). Idempotent.
 */
export function useDismissOrganizationTypeConflict() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contactId,
      ...body
    }: { contactId: string } & DismissOrganizationTypeConflictRequest) => {
      const response = await apiResponse(
        `/api/finance/organization-type-conflicts/${contactId}/dismiss`,
        { method: 'POST', json: body },
      );
      if (response.status === 409) throw new Error(CLIENT_CONFLICT_MARKER);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to dismiss organization-type conflict');
      }
      // 204 No Content — no payload.
    },
    onSuccess: () => {
      invalidateResolved(queryClient);
    },
  });
}
